import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Users2, Briefcase, FileText, ShieldCheck, Layers } from 'lucide-react';

interface Overview { insurers: number; users: number; active_users: number; brokers: number; surveys: number; import_jobs: number; last_audit_at: string | null }

const Tile: React.FC<{ label: string; value: number | string; icon: React.ReactNode; sub?: string }> = ({ label, value, icon, sub }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-raptor-navy/60 font-body">{label}</div>
          <div className="font-tabular text-3xl font-bold text-raptor-navy mt-1">{value}</div>
          {sub && <div className="text-[11px] text-raptor-navy/50 mt-1 font-body">{sub}</div>}
        </div>
        <div className="w-9 h-9 rounded-md bg-raptor-blue/10 flex items-center justify-center text-raptor-blue">{icon}</div>
      </div>
    </CardContent>
  </Card>
);

export const MasterOverview: React.FC = () => {
  const [o, setO] = useState<Overview | null>(null);
  useEffect(() => { api.get<Overview>('/master/overview').then((r) => setO(r.data)); }, []);
  return (
    <div className="space-y-6 animate-fade-in" data-testid="master-overview">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Platform overview</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Live figures across every insurer tenant on the platform. Use the sections in the sidebar to manage insurers, users, and audit history.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Tile label="Insurers" value={o?.insurers ?? 0} icon={<Building2 size={18} />} />
        <Tile label="Users" value={o?.users ?? 0} icon={<Users2 size={18} />} sub={`${o?.active_users ?? 0} active`} />
        <Tile label="Brokers" value={o?.brokers ?? 0} icon={<Briefcase size={18} />} />
        <Tile label="Completed surveys" value={o?.surveys ?? 0} icon={<Layers size={18} />} />
        <Tile label="Import jobs" value={o?.import_jobs ?? 0} icon={<FileText size={18} />} />
        <Tile label="Last audit event" value={o?.last_audit_at ? new Date(o.last_audit_at).toLocaleDateString() : '—'} icon={<ShieldCheck size={18} />} sub={o?.last_audit_at ? new Date(o.last_audit_at).toLocaleTimeString() : ''} />
      </div>

      <Card>
        <CardHeader><CardTitle>What is the Master Admin portal?</CardTitle></CardHeader>
        <CardContent className="text-sm text-raptor-navy/80 font-body space-y-2">
          <p>The Master Admin portal sits above every insurer tenant on the platform. From here you can create new insurers, manage users across the entire estate, review the full audit trail, and switch into any insurer's operating context when hands-on support is required.</p>
          <p>Every action taken from this portal is written to the append-only audit log, including origin IP address and browser fingerprint, satisfying the SOC 2 Common Criteria for logical-access monitoring.</p>
        </CardContent>
      </Card>
    </div>
  );
};
